import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import AvDialog from '@components/AvDialog';
import { flex } from '@fabric-ui/theme';
import { noop } from '@fabric-ui/utils';
import { Box, Button } from '@mui/material';
import { useMatch } from '@tanstack/react-router';
import { useBlocker } from '@utils/AvRouter';

export interface NavigationBlockerContextProps {
  setShouldBlockNavigation: (key: string, options: BlockerOptions) => void;
  clearNavigationBlocker: (key: string) => void;
}

export const NavigationBlockerContext = React.createContext<NavigationBlockerContextProps>({} as NavigationBlockerContextProps);

export interface BlockerOptions {
  isDirty: boolean;
  onDiscard?: () => void;
  modalContent?: ICustomModalProps;
}

interface ICustomModalProps {
  title?: string;
  body?: ReactNode;
  discardText?: string;
  confirmText?: string;
}

interface INavigationBlockerModal extends ICustomModalProps {
  open: boolean;
  onClose: () => void;
  onDiscard?: () => void;
}

export const NavigationBlockerModal = ({
  onDiscard,
  onClose,
  open = false,
  title = 'Unsaved Changes',
  body = <div>You are about to leave this page. Your changes are unsaved.</div>,
  discardText = 'Discard',
  confirmText = 'Keep Editing',
}: INavigationBlockerModal) => {
  const footer = (
    <Box sx={{ ...flex.justifyEndCenter, gap: 2 }}>
      <Button onClick={onDiscard}>{discardText}</Button>
      <Button variant="contained" onClick={onClose}>
        {confirmText}
      </Button>
    </Box>
  );

  return (
    <AvDialog title={title} titleVariant="warning" onClose={onClose} footer={footer} open={open} size="md">
      {body}
    </AvDialog>
  );
};

export const NavigationBlockerProvider = ({ children }: React.PropsWithChildren = {}) => {
  const [blockers, setBlockers] = useState<Record<string, BlockerOptions>>({});
  const hasBlockers = useMemo(() => Object.values(blockers).some(({ isDirty }) => isDirty), [blockers]);
  const globalNotFound = useMatch({ strict: false, select: ({ globalNotFound }) => globalNotFound });

  const { proceed, reset, status } = useBlocker({
    shouldBlockFn: () => hasBlockers,
    // TODO: @tanstack/react-router bug, remove when the following issue is resolved: https://github.com/TanStack/router/issues/3761
    enableBeforeUnload: hasBlockers,
    withResolver: true,
    disabled: globalNotFound,
  } as const);

  const setShouldBlockNavigation = useCallback((key: string, value: BlockerOptions) => {
    setBlockers(prev => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const clearNavigationBlocker = useCallback((key: string) => {
    setBlockers(prev => {
      const { [key]: blocker, ...rest } = prev;
      return rest;
    });
  }, []);

  const handleBlockersUnload = useCallback(() => {
    Object.values(blockers).forEach(({ isDirty, onDiscard }) => {
      if (isDirty && onDiscard) {
        onDiscard();
      }
    });
    setBlockers({});
  }, [blockers]);

  useEffect(() => {
    window.addEventListener('unload', handleBlockersUnload);

    return () => {
      window.removeEventListener('unload', handleBlockersUnload);
    };
  }, [handleBlockersUnload]);

  const activeBlocker = useMemo(() => Object.values(blockers).findLast(b => b.isDirty), [blockers]);
  const value = useMemo(() => ({ setShouldBlockNavigation, clearNavigationBlocker }), [setShouldBlockNavigation, clearNavigationBlocker]);

  return (
    <NavigationBlockerContext.Provider value={value}>
      <NavigationBlockerModal
        {...(activeBlocker?.modalContent ?? {})}
        onClose={reset ?? noop}
        open={status === 'blocked'}
        onDiscard={() => {
          const activeKey = Object.entries(blockers).findLast(([, value]) => value.isDirty)?.[0];
          if (activeKey) {
            activeBlocker?.onDiscard?.();
            clearNavigationBlocker(activeKey);
          }
          proceed?.();
        }}
      />
      {children}
    </NavigationBlockerContext.Provider>
  );
};
